import { useTranslation } from 'react-i18next';
import { Card } from '../../shared/ui';
import {
  QualityTrendChart,
  IssueDistributionChart,
  HealthRadarChart
} from '../../features/analysis-visuals';
import { DashboardMetrics } from '../../entities/report';

interface LocalStats {
  critical: number;
  bugs: number;
  vulnerabilities: number;
  codeSmells: number;
  totalIssues: number;
  totalAnalyses: number;
}

interface ChartsSectionProps {
  className?: string;
  metrics?: DashboardMetrics;
  localStats?: LocalStats;
}

export function ChartsSection({ className = '', metrics, localStats }: ChartsSectionProps) {
  const { t } = useTranslation();

  // Backend now returns total_issues as an object { critical, high, medium, low }
  const apiSeverity = metrics?.total_issues;

  // Merge local store stats with API metrics — local takes priority for counts
  const critical = ((localStats?.vulnerabilities || 0) + (apiSeverity?.critical || 0)) || 0;
  const high = ((localStats?.bugs || 0) + (apiSeverity?.high || 0)) || 0;
  const medium = ((localStats?.codeSmells || 0) + (apiSeverity?.medium || 0)) || 0;
  const low = (apiSeverity?.low || 0) || 0;
  const totalIssues = (critical + high + medium + low) || 0;

  const stats = { critical, high, medium, low };

  // Health score: prefer API's average_health_score, otherwise compute from issues
  const healthScore = (metrics?.average_health_score ?? (totalIssues > 0 ? Math.max(0, 100 - (critical * 5 + high * 2 + medium * 0.5)) : 0)) || 0;

  const summaryForCharts = totalIssues > 0 || metrics ? {
    health_score: healthScore,
    coverage: 85,
    issues_by_severity: stats,
    issues_by_category: {
      security: critical + high,
      performance: medium,
      documentation: low,
      bugs: high,
      code_quality: medium,
    }
  } : undefined;

  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 gap-6 ${className}`}>
      {/* Trends Chart — aggregates all local analyses */}
      <Card corners="asymmetric" padding="lg">
        <div className="flex flex-col h-full">
          <h3 className="font-semibold text-text-primary font-mono mb-1">{t('charts.codeQualityTrends')}</h3>
          <p className="text-sm text-text-muted font-mono mb-6">{t('charts.last4Weeks')}</p>
          <div className="h-64 w-full">
            <QualityTrendChart projectId="dashboard-aggregated" />
          </div>
        </div>
      </Card>

      {/* Issues Distribution */}
      <Card corners="asymmetric-inverse" padding="lg">
        <div className="flex flex-col h-full">
          <h3 className="font-semibold text-text-primary font-mono mb-1">{t('charts.issuesDistribution')}</h3>
          <p className="text-sm text-text-muted font-mono mb-6">{t('charts.byType')}</p>
          <div className="h-64 w-full">
            {totalIssues > 0 ? (
              <IssueDistributionChart stats={stats} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-xs text-text-muted font-mono opacity-60">
                  Analiza proyectos para ver la distribución
                </p>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Code Health Radar — Full Width */}
      <Card className="md:col-span-2" corners="asymmetric" padding="lg">
        <div className="flex flex-col h-full">
          <h3 className="font-semibold text-text-primary font-mono mb-1">{t('charts.codeHealthRadar')}</h3>
          <p className="text-sm text-text-muted font-mono mb-6">{t('charts.overallMetrics')}</p>
          <div className="h-80 w-full">
            {summaryForCharts ? (
              <HealthRadarChart summary={summaryForCharts as any} />
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-xs text-text-muted font-mono opacity-60">
                  Sin datos de análisis aún
                </p>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
